/**
 * Deeper fixtures for the per-agent lenses + cross-agent views (Phases 2–3): detailed
 * runs, simulate results, the cross-agent approval queue (with provenance flags), and the
 * activity feed. Kept separate from fixtures.ts so the core roster data stays small.
 */
import { FIXTURE_AGENTS, type Reversibility } from "./fixtures";

// ── Runs (deep) ──────────────────────────────────────────────────────────────
export type RunStatus =
  | "approved"
  | "auto"
  | "pending"
  | "rejected"
  | "skipped";
export type ObjectType =
  | "Contact"
  | "Event"
  | "Task"
  | "Note"
  | "Email"
  | "Stat";

export interface RunActionTaken {
  label: string;
  reversibility: Reversibility;
  /** Resulting object, e.g. "Draft to Dana W." — clickable in the real app. */
  objectLabel?: string;
  objectType?: ObjectType;
}

/**
 * A curated run milestone — one legible beat of the run-as-timeline (design §6).
 * The four kinds map to "what triggered it → what it read → what it decided →
 * what it did / is waiting on", each expandable inline for detail.
 */
export type MilestoneKind = "trigger" | "read" | "decided" | "did" | "waiting";

export interface RunMilestone {
  id: string;
  kind: MilestoneKind;
  /** One-line headline shown collapsed, e.g. "Drafted a reply with 3 slots". */
  label: string;
  /** Optional expanded detail (a sentence, or a bulleted list rendered one-per-line). */
  detail?: string;
  detailList?: string[];
  /** Short relative time for the beat, e.g. "9:12". */
  when?: string;
  /** Reversibility marker for action milestones (free / sign-off). */
  reversibility?: Reversibility;
  objectLabel?: string;
  objectType?: ObjectType;
}

/**
 * A prepared, awaiting-sign-off action embedded in the run (design §5.2–5.4). It
 * carries the EXACT prepared payload + provenance so the inline sign-off card can
 * render the real draft in the run's timeline. Mirrors the `agent_approval` record
 * the live engine writes; the inline card decides it through the same decide path.
 */
export interface RunSignOff {
  /** The approval id the decide endpoint records against. */
  approvalId: string;
  actionType: string;
  /** Prepared email payload (for `email.send`); the inline card renders the real draft. */
  email?: { to: string; subject: string; body: string; sendTime?: string };
  /** Structured args for non-email actions, rendered as a clear summary. */
  args?: Record<string, unknown>;
  /** Risky params traced to untrusted input — amber treatment. Load-bearing. */
  provenanceFlags: ProvenanceFlag[];
  /** A scoped standing-grant the sign-off offers. */
  grantOffer?: string;
}

export interface AgentRunDetail {
  id: string;
  agentId: string;
  when: string;
  status: RunStatus;
  trusted?: boolean;
  objectType?: ObjectType;
  /** Audit trail. */
  trigger: string;
  read: string[];
  decided: string;
  did: RunActionTaken[];
  /** Curated timeline beats — drives the run-as-timeline view (design §6). */
  milestones?: RunMilestone[];
  /** Present when the run is suspended awaiting sign-off — drives the inline card (design §5). */
  awaitingSignOff?: RunSignOff;
}

export const FIXTURE_RUNS: Record<string, AgentRunDetail[]> = {
  "a-follow-up": [
    {
      id: "fr-pending",
      agentId: "a-follow-up",
      when: "Today 9:12",
      status: "pending",
      objectType: "Email",
      trigger: "New email from Dana W. (Contact tagged investor)",
      read: [
        "The email thread with Dana W.",
        "Your calendar availability for next week",
      ],
      decided:
        "The email asks to meet, so I drafted a reply proposing three open slots.",
      did: [
        {
          label: "Drafted reply with 3 proposed slots",
          reversibility: "free",
          objectLabel: "Draft to Dana W.",
          objectType: "Email",
        },
        {
          label: "Send the email",
          reversibility: "sign-off",
          objectLabel: "Awaiting your sign-off",
          objectType: "Email",
        },
      ],
      milestones: [
        {
          id: "fr-pending-m1",
          kind: "trigger",
          when: "9:12",
          label: "Triggered by a new email from Dana W.",
          detail:
            "Dana W. is a Contact tagged investor, so this thread matched the agent's reactive trigger.",
        },
        {
          id: "fr-pending-m2",
          kind: "read",
          when: "9:12",
          label: "Read the thread and your calendar",
          detailList: [
            "The email thread with Dana W.",
            "Your calendar availability for next week",
          ],
        },
        {
          id: "fr-pending-m3",
          kind: "decided",
          when: "9:12",
          label: "Decided to propose three meeting slots",
          detail:
            "The email explicitly asks to meet, so I drafted a reply offering three open slots from your calendar.",
        },
        {
          id: "fr-pending-m4",
          kind: "did",
          when: "9:12",
          label: "Drafted the reply",
          reversibility: "free",
          objectLabel: "Draft to Dana W.",
          objectType: "Email",
          detail: "Saved as a draft — nothing has been sent.",
        },
        {
          id: "fr-pending-m5",
          kind: "waiting",
          when: "now",
          label: "Waiting on your sign-off to send",
          reversibility: "sign-off",
          detail:
            "Sending is a consequential action, so the run is paused until you approve, edit, or reject the draft below.",
        },
      ],
      awaitingSignOff: {
        approvalId: "ap-dana",
        actionType: "email.send",
        email: {
          to: "dana@vc.com",
          subject: "Re: quick sync next week?",
          body: "Hi Dana,\n\nHappy to find time — here are three slots that work on my side:\n\n• Tue 11:00 PT\n• Wed 14:00 PT\n• Thu 09:30 PT\n\nWhich is easiest?\n\nBest,\nAlfie",
          sendTime: "now",
        },
        provenanceFlags: [
          {
            param: "recipient",
            note: "new recipient — first time you’ve emailed this address",
          },
        ],
        grantOffer: "Don’t ask again for replies to Contacts I already know",
      },
    },
    {
      id: "fr1",
      agentId: "a-follow-up",
      when: "Mon 4:01",
      status: "approved",
      objectType: "Email",
      trigger: "New email from Priya N. (Contact tagged investor)",
      read: ["The thread with Priya N.", "Your calendar availability"],
      decided:
        "Asked to meet — drafted a reply with three slots; you approved the send.",
      did: [
        {
          label: "Drafted reply",
          reversibility: "free",
          objectLabel: "Reply to Priya N.",
          objectType: "Email",
        },
        {
          label: "Sent the email",
          reversibility: "sign-off",
          objectLabel: "Sent to Priya N.",
          objectType: "Email",
        },
      ],
      milestones: [
        {
          id: "fr1-m1",
          kind: "trigger",
          when: "Mon 4:01",
          label: "Triggered by a new email from Priya N.",
          detail: "Priya N. is a Contact tagged investor.",
        },
        {
          id: "fr1-m2",
          kind: "read",
          when: "Mon 4:01",
          label: "Read the thread and your calendar",
          detailList: ["The thread with Priya N.", "Your calendar availability"],
        },
        {
          id: "fr1-m3",
          kind: "decided",
          when: "Mon 4:01",
          label: "Decided to propose three slots",
          detail: "The email asked to meet next week.",
        },
        {
          id: "fr1-m4",
          kind: "did",
          when: "Mon 4:02",
          label: "Sent the reply after your sign-off",
          reversibility: "sign-off",
          objectLabel: "Sent to Priya N.",
          objectType: "Email",
          detail: "You approved the send.",
        },
      ],
    },
    {
      id: "fr2",
      agentId: "a-follow-up",
      when: "Fri 11:20",
      status: "auto",
      trusted: true,
      objectType: "Email",
      trigger: "New email from Marcus H. (Contact you already know)",
      read: ["The thread with Marcus H.", "Your calendar availability"],
      decided:
        "Recipient is a known Contact (grant active), so I sent the reply without asking.",
      did: [
        {
          label: "Drafted reply",
          reversibility: "free",
          objectLabel: "Reply to Marcus H.",
          objectType: "Email",
        },
        {
          label: "Sent the email",
          reversibility: "sign-off",
          objectLabel: "Sent to Marcus H.",
          objectType: "Email",
        },
      ],
    },
    {
      id: "fr3",
      agentId: "a-follow-up",
      when: "Thu 15:40",
      status: "skipped",
      objectType: "Email",
      trigger: "New email from a Contact tagged investor",
      read: ["The thread"],
      decided: "The email did not ask to meet, so I took no action.",
      did: [],
    },
  ],
  "a-morning-brief": [
    {
      id: "mb1",
      agentId: "a-morning-brief",
      when: "Today 7:00",
      status: "auto",
      trusted: true,
      objectType: "Note",
      trigger: "Scheduled — every day at 7:00",
      read: ["Today’s calendar", "Unread inbox", "Open tasks due today"],
      decided: "Assembled the day-at-a-glance and posted it to your feed.",
      did: [
        {
          label: "Wrote the morning brief",
          reversibility: "free",
          objectLabel: "Today’s brief",
          objectType: "Note",
        },
      ],
      milestones: [
        {
          id: "mb1-m1",
          kind: "trigger",
          when: "7:00",
          label: "Triggered on schedule — every day at 7:00",
        },
        {
          id: "mb1-m2",
          kind: "read",
          when: "7:00",
          label: "Read today’s calendar, inbox, and open tasks",
          detailList: ["Today’s calendar", "Unread inbox", "Open tasks due today"],
        },
        {
          id: "mb1-m3",
          kind: "did",
          when: "7:00",
          label: "Posted the morning brief to your feed",
          reversibility: "free",
          objectLabel: "Today’s brief",
          objectType: "Note",
          detail: "3 meetings, 1 needs prep.",
        },
      ],
    },
  ],
  // A stuck run — the blocking_question notification routes here.
  "a-weekly-digest": [
    {
      id: "wr1",
      agentId: "a-weekly-digest",
      when: "Fri 16:00",
      status: "pending",
      objectType: "Note",
      trigger: "Scheduled — every Friday at 16:00",
      read: ["The week’s activity across your workspaces"],
      decided:
        "Drafted the digest, but I’m not sure which team to send it to — I need your call before going further.",
      did: [
        {
          label: "Drafted the weekly digest",
          reversibility: "free",
          objectLabel: "This week’s digest",
          objectType: "Note",
        },
      ],
      milestones: [
        {
          id: "wr1-m1",
          kind: "trigger",
          when: "16:00",
          label: "Triggered on schedule — every Friday at 16:00",
        },
        {
          id: "wr1-m2",
          kind: "read",
          when: "16:00",
          label: "Read the week’s activity",
          detailList: ["The week’s activity across your workspaces"],
        },
        {
          id: "wr1-m3",
          kind: "did",
          when: "16:01",
          label: "Drafted the digest",
          reversibility: "free",
          objectLabel: "This week’s digest",
          objectType: "Note",
        },
        {
          id: "wr1-m4",
          kind: "waiting",
          when: "now",
          label: "Stuck — which team should this go to?",
          detail:
            "Two teams matched “team” and I can’t tell which you meant. Answer in chat and the run continues from here.",
        },
      ],
    },
  ],
  // A failed run — the done(failure) notification routes here.
  "a-inbox-triager": [
    {
      id: "ir1",
      agentId: "a-inbox-triager",
      when: "12m ago",
      status: "rejected",
      objectType: "Email",
      trigger: "New email arrived",
      read: ["The new thread"],
      decided:
        "Tried to classify and label the thread, but the mail provider was unreachable.",
      did: [],
      milestones: [
        {
          id: "ir1-m1",
          kind: "trigger",
          when: "12m ago",
          label: "Triggered by a new email",
        },
        {
          id: "ir1-m2",
          kind: "did",
          when: "12m ago",
          label: "Failed — couldn’t reach the mail provider",
          detail:
            "The label step errored on a provider timeout. Nothing was changed. The run ended without acting.",
        },
      ],
    },
  ],
};

// ── Simulate ─────────────────────────────────────────────────────────────────
export type SimWindow = "7d" | "14d";
export type SimKind = "would-draft" | "would-flag" | "would-skip";
export interface SimOutcome {
  id: string;
  kind: SimKind;
  label: string;
  detail?: string;
}
export interface SimulateResult {
  window: SimWindow;
  ranAt: string;
  outcomes: SimOutcome[];
}

const SIM_BASE: Record<string, SimOutcome[]> = {
  "a-follow-up": [
    {
      id: "s1",
      kind: "would-draft",
      label: "Reply to Dana W.",
      detail: "asked to meet — 3 slots proposed",
    },
    {
      id: "s2",
      kind: "would-draft",
      label: "Reply to Priya N.",
      detail: "asked to meet next week",
    },
    {
      id: "s3",
      kind: "would-draft",
      label: "Reply to Owen R.",
      detail: "asked for a quick call",
    },
    {
      id: "s4",
      kind: "would-draft",
      label: "Reply to Lena K.",
      detail: "asked to grab coffee",
    },
    {
      id: "s5",
      kind: "would-flag",
      label: "Email from “investor-relations@unknown.co”",
      detail: "new domain — flagged, not sent",
    },
    {
      id: "s6",
      kind: "would-skip",
      label: "Newsletter from Bessemer",
      detail: "no meeting request",
    },
    {
      id: "s7",
      kind: "would-skip",
      label: "Reply from Marcus H.",
      detail: "thread already resolved",
    },
    {
      id: "s8",
      kind: "would-skip",
      label: "Internal thread",
      detail: "sender not tagged investor",
    },
  ],
};

export function simulateAgent(
  agentId: string,
  window: SimWindow,
): SimulateResult {
  const all = SIM_BASE[agentId] ?? [
    {
      id: "d1",
      kind: "would-draft",
      label: "A drafted action",
      detail: "sample outcome",
    },
    {
      id: "k1",
      kind: "would-skip",
      label: "A skipped item",
      detail: "condition not met",
    },
  ];
  const outcomes =
    window === "7d"
      ? all
      : [
          ...all,
          {
            id: "x",
            kind: "would-skip" as SimKind,
            label: "Older low-signal threads",
            detail: "+6 skipped",
          },
        ];
  return { window, ranAt: new Date().toISOString(), outcomes };
}

// ── Approvals (cross-agent queue) ────────────────────────────────────────────
export interface ProvenanceFlag {
  param: string;
  note: string;
}
export interface ApprovalItem {
  id: string;
  // Nullable for agent-less manual/ad-hoc durable runs (ADR-025) — rendered with
  // a "Manual run" label + neutral colour instead of a looked-up agent (#4065).
  agentId: string | null;
  agentName: string;
  agentColor: string;
  actionType: string;
  email?: { to: string; subject: string; body: string; sendTime?: string };
  why: { trigger: string; read: string[] };
  /** Risky params traced to untrusted input — rendered with an amber marker. Load-bearing. */
  provenanceFlags: ProvenanceFlag[];
  /** A scoped standing-grant the approval offers. */
  grantOffer?: string;
}

const agentMeta = (id: string) => {
  const a = FIXTURE_AGENTS.find((x) => x.id === id);
  return { agentName: a?.name ?? id, agentColor: a?.color ?? "#7a7f85" };
};

export const FIXTURE_APPROVALS: ApprovalItem[] = [
  {
    id: "ap-dana",
    agentId: "a-follow-up",
    ...agentMeta("a-follow-up"),
    actionType: "email.send",
    email: {
      to: "investor@vc.com",
      subject: "Re: quick sync next week?",
      body: "Hi Dana,\n\nHappy to find time — here are three slots that work on my side:\n\n• Tue 11:00 PT\n• Wed 14:00 PT\n• Thu 09:30 PT\n\nWhich is easiest?\n\nBest,\nAlfie",
      sendTime: "now",
    },
    why: {
      trigger: "New email from Dana W. (Contact tagged investor)",
      read: ["The thread with Dana W.", "Your calendar availability"],
    },
    provenanceFlags: [],
    grantOffer: "Don’t ask again for replies to Contacts I already know",
  },
  {
    id: "ap-unknown",
    agentId: "a-follow-up",
    ...agentMeta("a-follow-up"),
    actionType: "email.send",
    email: {
      to: "invoices@billing-portal.app",
      subject: "Re: payment details",
      body: "Hi,\n\nSending over the details you asked for.\n\nBest,\nAlfie",
      sendTime: "now",
    },
    why: {
      trigger: "New email asking to send payment details",
      read: ["An email whose body requested this recipient"],
    },
    // The recipient came from the *email body*, not the user's instruction → must be flagged.
    provenanceFlags: [
      { param: "recipient", note: "from the email, not your Contacts" },
    ],
  },
];

// ── Activity (cross-agent feed) ──────────────────────────────────────────────
export interface ActivityItem {
  id: string;
  when: string;
  /** Synthetic ISO timestamp (fixtures) for time-window filtering; live data supplies a real one. */
  ts?: string;
  agentId: string;
  agentName: string;
  agentColor: string;
  summary: string;
  status: RunStatus;
  trusted?: boolean;
}

export function buildActivity(): ActivityItem[] {
  const items: ActivityItem[] = [];
  const now = Date.now();
  let idx = 0;
  for (const a of FIXTURE_AGENTS) {
    for (const r of a.runs) {
      // Synthetic timestamp (fixtures): spread items back ~7h apart so the Today / This week /
      // This month filters have variety to act on. Live data supplies the real `ts`.
      const ts = new Date(now - idx * 7 * 3_600_000).toISOString();
      idx++;
      items.push({
        id: `${a.id}-${r.id}`,
        when: r.when,
        ts,
        agentId: a.id,
        agentName: a.name,
        agentColor: a.color,
        summary: r.summary,
        status: r.status,
        trusted: r.trusted,
      });
    }
  }
  return items;
}

// ── Notifications (the three typed signals — design §4) ──────────────────────
/**
 * The three typed notification signals, in priority order (design §4):
 *   `blocking_question` > `sign_off` > `done`.
 * A run blocked on a decision outranks a prepared action awaiting sign-off, which
 * outranks a purely informational completion. `NOTIF_PRIORITY` keys the feed sort.
 */
export type NotificationKind = "blocking_question" | "sign_off" | "done";

/** Lower index = higher priority. Drives the feed sort + which signal surfaces first. */
export const NOTIF_PRIORITY: Record<NotificationKind, number> = {
  blocking_question: 0,
  sign_off: 1,
  done: 2,
};

/** A `done` notification reports the run's outcome (success or failure). */
export type NotificationOutcome = "success" | "failure";

export interface AgentNotification {
  id: string;
  kind: NotificationKind;
  /** The agent the run belongs to — drives the colour dot + routing target. */
  agentId: string;
  agentName: string;
  agentColor: string;
  /** The run this notification is about — clicking opens that run's timeline. */
  runId: string;
  /** One-line plain-language summary, e.g. "Boulevard run needs your OK to send this reply". */
  summary: string;
  /** Short relative time, e.g. "9:12" / "2h ago". */
  when: string;
  /** For `done`: whether the run succeeded or failed (informational treatment). */
  outcome?: NotificationOutcome;
  /** For `sign_off`: the approval id the run is suspended on (mirrors the run's `awaitingSignOff`). */
  approvalId?: string;
}

/** Unresolved signals (the ones still needing the human) — `sign_off` + `blocking_question`. */
export function isUnresolvedNotification(n: AgentNotification): boolean {
  return n.kind === "sign_off" || n.kind === "blocking_question";
}

const agentMetaFull = (id: string) => {
  const a = FIXTURE_AGENTS.find((x) => x.id === id);
  return {
    agentId: id,
    agentName: a?.name ?? id,
    agentColor: a?.color ?? "#7a7f85",
  };
};

/**
 * Notification fixtures covering all three signals. Derived from the run fixtures
 * where natural: the `sign_off` is tied to the existing awaiting-sign-off run
 * (`fr-pending` → approval `ap-dana`) so resolving it in the run's inline card and
 * the notification stay consistent. `blocking_question`, `done` success, and `done`
 * failure round out the set.
 */
export function buildNotifications(): AgentNotification[] {
  return [
    // sign_off — tied to the existing awaiting-sign-off run (fr-pending / ap-dana).
    {
      id: "n-signoff-dana",
      kind: "sign_off",
      ...agentMetaFull("a-follow-up"),
      runId: "fr-pending",
      approvalId: "ap-dana",
      summary: "Follow-up chaser needs your OK to send a reply to Dana W.",
      when: "9:12",
    },
    // blocking_question — a stuck run needing a plain-language decision.
    {
      id: "n-block-digest",
      kind: "blocking_question",
      ...agentMetaFull("a-weekly-digest"),
      runId: "wr1",
      summary: "Weekly digest is stuck — needs your call on which team to send to.",
      when: "Fri 16:02",
    },
    // done (success) — informational completion.
    {
      id: "n-done-brief",
      kind: "done",
      ...agentMetaFull("a-morning-brief"),
      runId: "mb1",
      outcome: "success",
      summary: "Morning brief finished — posted today’s day-at-a-glance.",
      when: "7:00",
    },
    // done (failure) — informational, with the failure outcome.
    {
      id: "n-done-triage-fail",
      kind: "done",
      ...agentMetaFull("a-inbox-triager"),
      runId: "ir1",
      outcome: "failure",
      summary: "Inbox triager failed — couldn’t reach the mail provider.",
      when: "12m ago",
    },
  ];
}
